import { format, parseISO, startOfISOWeek, subWeeks } from 'date-fns';
import { FileProcessingStatus } from '../domain/enums';
import { IngestionSummary, ProviderRecentCounts } from '../domain/types';
import { OUTCOME_CLASSES } from '../types';
import {
  countOutcomes,
  describeOutcomeCounts,
  EMPTY_OUTCOME_COUNTS,
} from './outcome-class.utils';

/** Weeks of history the provider detail chart shows. */
export const WEEKLY_OUTCOME_WEEKS = 12;

const WEEK_KEY_FORMAT = 'yyyy-MM-dd';

export interface WeeklyOutcomeBucket {
  /** Local ISO-week Monday as `yyyy-MM-dd`; the chart's key and label source. */
  weekStart: string;
  counts: ProviderRecentCounts;
  /** Files in the week that belong to an outcome class (CANCELLED excluded). */
  total: number;
}

interface BucketOptions {
  weeks?: number;
  now?: Date;
}

interface WeekStatuses {
  weekStart: string;
  statuses: FileProcessingStatus[];
}

const weekKey = (date: Date): string =>
  format(startOfISOWeek(date), WEEK_KEY_FORMAT);

const sumCounts = (counts: ProviderRecentCounts): number =>
  Object.values(counts).reduce((total, count) => total + count, 0);

/**
 * Buckets ingestions into the last `weeks` local ISO weeks, oldest first,
 * zero-filled. Local weeks match how `printDateAndTime` renders timestamps
 * elsewhere in the module, so a file shown as "22 Sep" lands in the week
 * that starts on the Monday before it. Files outside the window are dropped.
 */
export const bucketWeeklyOutcomes = (
  ingestions: IngestionSummary[],
  { weeks = WEEKLY_OUTCOME_WEEKS, now = new Date() }: BucketOptions = {},
): WeeklyOutcomeBucket[] => {
  const weekList: WeekStatuses[] = Array.from(
    { length: weeks },
    (_, index) => ({
      weekStart: weekKey(subWeeks(now, weeks - 1 - index)),
      statuses: [],
    }),
  );
  const byWeekStart = new Map(weekList.map(week => [week.weekStart, week]));

  ingestions.forEach(ingestion => {
    const week = byWeekStart.get(weekKey(new Date(ingestion.createdAt)));

    if (week !== undefined) {
      week.statuses.push(ingestion.status);
    }
  });

  return weekList.map(({ weekStart, statuses }) => {
    const counts = countOutcomes(statuses);

    return { weekStart, counts, total: sumCounts(counts) };
  });
};

/** '2026-09-21' → '21 Sep'. */
export const formatWeekStart = (weekStart: string): string =>
  format(parseISO(weekStart), 'd MMM');

const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

/** Per-class totals across every bucket in the window. */
export const sumWeeklyOutcomes = (
  buckets: WeeklyOutcomeBucket[],
): ProviderRecentCounts =>
  buckets.reduce(
    (totals, bucket) =>
      OUTCOME_CLASSES.reduce(
        (next, outcomeClass) => ({
          ...next,
          [outcomeClass]: next[outcomeClass] + bucket.counts[outcomeClass],
        }),
        totals,
      ),
    { ...EMPTY_OUTCOME_COUNTS },
  );

/** One sentence for the chart's `aria-label`. */
export const describeWeeklyOutcomes = (
  buckets: WeeklyOutcomeBucket[],
): string => {
  const weeks = plural(buckets.length, 'week');
  const totals = sumWeeklyOutcomes(buckets);
  const files = sumCounts(totals);

  if (files === 0) {
    return `No files over ${weeks}.`;
  }

  return `${plural(files, 'file')} over ${weeks}: ${describeOutcomeCounts(
    totals,
  )}.`;
};
